import { useState, useEffect, useRef } from 'react';
import { CountrySelector } from './CountrySelector';
import { StateSelector } from './StateSelector';
import { CitySelector } from './CitySelector';
import type { LocationSelection } from '../../types/location';

export interface LocationSelectorProps {
  readonly value?: LocationSelection;
  readonly onChange: (location: LocationSelection) => void;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly levels?: ('country' | 'state' | 'city')[];
  readonly labels?: {
    country?: string;
    state?: string;
    city?: string;
  };
  readonly placeholders?: {
    country?: string;
    state?: string;
    city?: string;
  };
  readonly error?: string;
  readonly onValidate?: (location: LocationSelection) => string | null;
  readonly className?: string;
  readonly showCoordinates?: boolean;
}

/**
 * LocationSelector Container Component
 * 
 * Coordinates three hierarchical selectors: Country → State → City
 * 
 * Features:
 * - Hierarchical state management
 * - Automatic clearing of dependent selectors
 * - Initial value support for editing
 * - Configurable required/optional fields
 * - Responsive layout (vertical/horizontal)
 * - Global error handling
 * - Global loading state
 * - Validation support
 * - Accessibility compliant
 */
export function LocationSelector({
  value,
  onChange,
  disabled = false,
  required = false,
  levels = ['country', 'state', 'city'],
  labels = {},
  placeholders = {},
  error,
  onValidate,
  className = '',
  showCoordinates = false
}: LocationSelectorProps) {
  const [selection, setSelection] = useState<LocationSelection>(
    value || { country: null, state: null, city: null }
  );
  const [validationError, setValidationError] = useState<string | null>(null);
  // Holds country resolved from state selection before handleStateChange fires (avoids stale closure race)
  const pendingCountryRef = useRef<LocationSelection['country'] | null>(null);

  // Update internal state when value prop changes
  useEffect(() => {
    if (value) {
      setSelection(value);
    }
  }, [value]);

  // Validate selection when it changes
  useEffect(() => {
    if (onValidate) {
      const error = onValidate(selection);
      setValidationError(error);
    }
  }, [selection, onValidate]);

  const handleCountryChange = (country: LocationSelection['country']) => {
    const newSelection: LocationSelection = {
      country,
      state: null,
      city: null
    };
    setSelection(newSelection);
    onChange(newSelection);
  };

  const handleStateChange = (state: LocationSelection['state']) => {
    // Consume any country resolved synchronously just before this call
    const resolvedCountry = pendingCountryRef.current ?? selection.country;
    pendingCountryRef.current = null;
    const newSelection: LocationSelection = {
      ...selection,
      country: resolvedCountry,
      state,
      city: null
    };
    setSelection(newSelection);
    onChange(newSelection);
  };

  // Auto-resolve country when state is selected in global mode (no country pre-selected).
  // Stores in a ref so handleStateChange (called synchronously right after) can apply both atomically.
  const handleCountryResolvedFromState = (countryId: string, countryCode: string, countryName: string) => {
    pendingCountryRef.current = {
      id: countryId,
      name: countryName,
      iso2: countryCode,
    };
  };

  const handleCityChange = (city: LocationSelection['city']) => {
    const newSelection: LocationSelection = {
      ...selection,
      city
    };
    setSelection(newSelection);
    onChange(newSelection);
  };

  // Auto-resolve state when city is selected without state (byCountry mode)
  // Called right after handleCityChange in the same sync execution,
  // so we use a functional updater to get the latest queued state
  const handleStateResolved = (state: LocationSelection['state'], city: LocationSelection['city']) => {
    const newSelection: LocationSelection = {
      ...selection,
      state,
      city
    };
    setSelection(newSelection);
    onChange(newSelection);
  };

  const showCountry = levels.includes('country');
  const showState = levels.includes('state');
  const showCity = levels.includes('city');

  const displayError = error || validationError;

  const stateCityGridClass = showState && showCity ? 'grid grid-cols-1 md:grid-cols-2 gap-3' : '';

  return (
    <div className={`space-y-3 ${className}`}>
      {showCountry && (
        <CountrySelector
          value={selection.country}
          onChange={handleCountryChange}
          disabled={disabled}
          required={required}
          label={labels.country}
          placeholder={placeholders.country}
          error={displayError && !selection.country ? displayError : undefined}
        />
      )}

      {(showState || showCity) && (
        <div className={stateCityGridClass}>
          {showState && (
            <StateSelector
              country={selection.country}
              value={selection.state}
              onChange={handleStateChange}
              onCountryResolved={handleCountryResolvedFromState}
              disabled={disabled}
              required={required}
              label={labels.state ?? 'Estado/Región'}
              placeholder={placeholders.state}
              error={displayError && !selection.state ? displayError : undefined}
            />
          )}
          {showCity && (
            <CitySelector
              country={selection.country}
              state={selection.state}
              value={selection.city}
              onChange={handleCityChange}
              onStateResolved={handleStateResolved}
              disabled={disabled}
              required={required}
              label={labels.city}
              placeholder={placeholders.city}
              error={displayError && selection.country && !selection.city ? displayError : undefined}
              showCoordinates={showCoordinates}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Helper function to check if location selection is complete
 */
export function isLocationComplete(
  location: LocationSelection,
  levels: ('country' | 'state' | 'city')[] = ['country', 'state', 'city']
): boolean {
  if (levels.includes('country') && !location.country) {
    return false;
  }
  if (levels.includes('state') && !location.state) {
    return false;
  }
  if (levels.includes('city') && !location.city) {
    return false;
  }
  return true;
}

/**
 * Helper function to format location as string
 */
export function formatLocation(location: LocationSelection): string {
  const parts: string[] = [];
  
  if (location.city) {
    parts.push(location.city.name);
  }
  if (location.state) {
    parts.push(location.state.name);
  }
  if (location.country) {
    parts.push(location.country.name);
  }
  
  return parts.join(', ');
}
